/**
 * Horizontal width allocation among columns.
 *
 * NOT the horizontal twin of `viewport.ts`, and the analogy is worth resisting:
 * the vertical axis is ONE scroll window over homogeneous rows, so constants
 * like CHROME_ROWS work there. The horizontal axis is ALLOCATION among
 * heterogeneous columns with different priorities, different floors, and
 * mode-gated existence. Different problem, different function.
 */
import { finiteOr } from "./finite.js";
/**
 * Allocate `total` columns of width among `cols`.
 *
 * Integer in, integer out, deterministic. Fractions are the caller's business:
 * a detail pane that wants 35% of the terminal computes that before calling.
 *
 * Mode-gated columns are the caller's business too — a column that is closed
 * this frame is simply OMITTED from `cols`. The allocator must not know about
 * modes, or it grows a mode model.
 */
/** Priority, defaulting a non-finite one to 0 so sorting stays total. */
const rank = (c) => finiteOr(c.priority, 0);
export function allocateWidths(total, cols) {
    // A non-finite total made `floorSum() <= budget` false, so every droppable
    // column was shed and then growth handed out nothing — failing OPEN in the
    // opposite direction to `lineWindow`'s. Zero is the honest fallback: no
    // budget means floors only. See `finite.ts`.
    const budget = Math.max(0, Math.floor(finiteOr(total, 0)));
    // Ascending priority = the order in which columns are sacrificed.
    const byYieldOrder = [...cols].sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
    const present = new Map();
    for (const spec of cols) {
        present.set(spec.id, {
            spec,
            floor: Math.max(0, Math.floor(finiteOr(spec.min, 0))),
            // Omitting `max` means UNBOUNDED, per the ColumnSpec contract. Defaulting
            // it to `preferred` instead silently capped growth, so the remainder had
            // nowhere to go and landed on a lower-priority column — the exact
            // inversion of the rule this function is supposed to implement.
            ceiling: spec.max === undefined
                ? Number.POSITIVE_INFINITY
                : Math.max(0, Math.floor(finiteOr(spec.max, 0))),
        });
    }
    const collapsed = [];
    const floorSum = () => [...present.values()].reduce((n, c) => n + c.floor, 0);
    // Shed until the floors fit. A breadcrumbed column has already yielded all it
    // can, so it is not a candidate twice; a "min" column never is.
    for (const spec of byYieldOrder) {
        if (floorSum() <= budget)
            break;
        const behaviour = spec.collapse ?? "drop";
        if (behaviour === "min")
            continue;
        const entry = present.get(spec.id);
        if (!entry)
            continue;
        if (behaviour === "breadcrumb") {
            const crumb = Math.max(0, Math.floor(finiteOr(spec.collapsedWidth ?? 1, 1)));
            if (entry.floor <= crumb)
                continue;
            entry.floor = crumb;
            entry.ceiling = crumb;
        }
        else {
            present.delete(spec.id);
        }
        collapsed.push(spec.id);
    }
    // Everyone still present starts at their floor. If the floors still exceed
    // the budget every remaining column is pinned, and the caller's renderer
    // clips — which is honest, and better than silently dropping a column the
    // caller declared unsacrificeable.
    const widths = {};
    for (const [id, c] of present)
        widths[id] = c.floor;
    let remaining = budget - floorSum();
    // Growth runs in DESCENDING priority: the most important column not yet at
    // its ceiling takes the slack. Two passes so nobody reaches `max` before
    // everybody has reached `preferred`.
    const byImportance = [...present.values()].sort((a, b) => rank(b.spec) - rank(a.spec) || a.spec.id.localeCompare(b.spec.id));
    for (const target of ["preferred", "ceiling"]) {
        for (const c of byImportance) {
            // POSITIVE predicates on both. `remaining <= 0` and `room <= 0` are FALSE
            // for NaN, so a single non-finite spec field poisoned `remaining` and then
            // every column after it received a NaN width — the same
            // NaN-turns-a-bounded-loop-unbounded failure as `lineWindow`'s, in a
            // second place. See `finite.ts`.
            if (!(remaining > 0))
                break;
            const cap = target === "preferred"
                ? Math.min(Math.floor(finiteOr(c.spec.preferred, 0)), c.ceiling)
                : c.ceiling;
            const room = cap - (widths[c.spec.id] ?? 0);
            if (!(room > 0))
                continue;
            const give = Math.min(room, remaining);
            widths[c.spec.id] = (widths[c.spec.id] ?? 0) + give;
            remaining -= give;
        }
    }
    return { widths, collapsed };
}
//# sourceMappingURL=width-alloc.js.map